/**
 * HTTP Client for auth API calls
 */

import {
  postLogin,
  postRegister,
  postSocial,
  requestOtp,
  verifyOtp,
  refresh,
  resetPassword,
  storeFcmToken,
  getSession,
  revokeSession,
  updateStatus,
  logout,
} from "./utils";

class HttpDataClient {
  /**
   * Generic request handler with consistent error handling
   * @param {Promise} apiCall - API call to execute
   * @returns {Promise} Formatted response or error
   */
  async #handleRequest(apiCall) {
    try {
      const response = await apiCall;
      return {
        success: true,
        data: response.data.data,
        status: response.status,
        message: response.data.message,
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || error.message,
        error: error.response?.data || error.message,
        status: error.response?.status || 500,
      };
    }
  }

  /**
   * Login user
   * @param {Object} params - Login parameters
   * @returns {Promise} Response from API
   */
  postLogin(params = {}) {
    return this.#handleRequest(postLogin(params));
  }

  /**
   * Register user
   * @param {Object} params - Registration parameters
   * @returns {Promise} Response from API
   */
  postRegister(params = {}) {
    return this.#handleRequest(postRegister(params));
  }

  /**
   * Social login (Google/Facebook)
   * @param {Object} params - Social login parameters
   * @returns {Promise} Response from API
   */
  postSocial(params = {}) {
    return this.#handleRequest(postSocial(params));
  }

  /**
   * Refresh token
   * @param {Object} params - Refresh token parameters
   * @returns {Promise} Response from API
   */
  refresh(params = {}) {
    return this.#handleRequest(refresh(params));
  }

  /**
   * Request OTP
   * @param {Object} params - OTP request parameters
   * @returns {Promise} Response from API
   */
  requestOtp(params = {}) {
    return this.#handleRequest(requestOtp(params));
  }

  /**
   * Verify OTP
   * @param {Object} params - OTP verification parameters
   * @returns {Promise} Response from API
   */
  verifyOtp(params = {}) {
    return this.#handleRequest(verifyOtp(params));
  }

  /**
   * Reset Password
   * @param {Object} params - Password reset parameters
   * @returns {Promise} Response from API
   */
  resetPassword(params = {}) {
    return this.#handleRequest(resetPassword(params));
  }

  /**
   * Store FCM Token
   * @param {Object} params - FCM token parameters
   * @returns {Promise} Response from API
   */
  storeFcmToken(params = {}) {
    return this.#handleRequest(storeFcmToken(params));
  }

  /**
   * Get Session
   * @returns {Promise} Response from API
   */
  getSession() {
    return this.#handleRequest(getSession());
  }

  /**
   * Revoke Session
   * @param {Object} params - Session revoke parameters
   * @returns {Promise} Response from API
   */
  revokeSession(params = {}) {
    return this.#handleRequest(revokeSession(params));
  }

  /**
   * Update Status
   * @param {Object} params - Status update parameters
   * @returns {Promise} Response from API
   */
  updateStatus(params = {}) {
    return this.#handleRequest(updateStatus(params));
  }

  /**
   * Logout user
   * @param {Object} params - Logout parameters
   * @returns {Promise} Response from API
   */
  logout(params = {}) {
    return this.#handleRequest(logout(params));
  }
}

const Auth = new HttpDataClient();
Object.freeze(Auth);

export default Auth;
